import { mimeTypes } from '@/shared/constants';
import { type DriveFile } from '@/shared/DriveTypes';
import { AddLinkOutlined, AddToDrive, CreateNewFolderOutlined } from '@mui/icons-material';
import { Menu, MenuItem } from '@mui/material';
import cx from 'classnames';
import { bindMenu, type PopupState } from 'material-ui-popup-state/hooks';
import { darkTheme, lightTheme } from '~/theme.css';
import { LEGACY, transitionDuration } from '~/utils/constants';
import { DrivePicker } from '~/utils/DrivePicker';
import { useMutateDriveFile } from '~/utils/hooks/hooks';
import { useStore, useTheme } from '~/utils/store';
import { type ModalType } from '~/utils/types';
import { isAFolder } from '~/utils/utils';
import { type AddNewLegacy } from '../AddMenu/AddNewContainer';
import * as styles from './AddMenuLegacy.css';

export const AddMenuLegacy = ({
  wiki,
  file,
  popupState,
  modalState,
  addNew,
}: {
  wiki: DriveFile;
  file: DriveFile;
  popupState: PopupState;
  modalState: PopupState;
  addNew?: AddNewLegacy;
}) => {
  const setModalType = useStore((state) => state.setModalType);
  const mutate = useMutateDriveFile();
  const theme = useTheme();
  const handleClick = (modal: ModalType) => {
    modalState.open();
    popupState.close();
    setModalType(modal);
  };
  return (
    <>
      <Menu
        {...bindMenu(popupState)}
        elevation={6}
        transitionDuration={transitionDuration}
        className={cx({
          [darkTheme]: theme === 'dark',
          [lightTheme]: theme === 'light',
        })}
        slotProps={{
          paper: {
            className: styles.menu,
            style: {
              marginTop: 6,
              marginLeft: 2,
              minWidth: 164,
            },
          },
        }}
        onClose={() => {
          popupState.close();
          queueMicrotask(() => {
            if (document.activeElement instanceof HTMLButtonElement) {
              document.activeElement.blur();
            }
          });
        }}
      >
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => handleClick('document')}
        >
          <img
            src="https://www.gstatic.com/images/branding/product/1x/docs_48dp.png"
            alt="Page"
            style={{ width: 22, height: 22 }}
          />
          Document
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => handleClick('sheet')}
        >
          <img
            src="https://www.gstatic.com/images/branding/product/1x/sheets_48dp.png"
            alt="Sheet"
            style={{ width: 22, height: 22 }}
          />
          Sheet
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => handleClick('slide')}
        >
          <img
            src="https://www.gstatic.com/images/branding/product/1x/slides_48dp.png"
            alt="Slide"
            style={{ width: 22, height: 22 }}
          />
          Slide
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => handleClick('form')}
        >
          <img
            src="https://www.gstatic.com/images/branding/product/1x/forms_48dp.png"
            alt="Form"
            style={{ width: 22, height: 22 }}
          />
          Form
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => handleClick('folder')}
        >
          <CreateNewFolderOutlined style={{ fontSize: 22, color: '#8F8F8F' }} />
          Folder
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => handleClick('link')}
        >
          <AddLinkOutlined style={{ fontSize: 24, color: '#8f8f8f' }} />
          Link
        </MenuItem>
        <MenuItem
          className={styles.menuItem}
          focusVisibleClassName={styles.menuItemFocusVisible}
          onClick={() => {
            popupState.close();
            void DrivePicker.open('file', {
              wiki,
              onAction: (data) => {
                if (data.action !== 'picked') return;
                const pickedFile = data.docs[0];
                if (!pickedFile) throw new Error('No file selected');
                const parents = isAFolder(file) ? ([file.id] satisfies [string]) : file?.parents;
                if (!parents) throw new Error('No parent found');
                if (LEGACY) {
                  addNew?.(pickedFile.name, parents, 'shortcut', pickedFile.id);
                } else {
                  mutate('create', {
                    name: pickedFile.name,
                    mimeType: mimeTypes.shortcut,
                    parents,
                    shortcutDetails: {
                      targetId: pickedFile.id,
                      targetMimeType: pickedFile.mimeType,
                    },
                  });
                }
              },
            });
          }}
        >
          <AddToDrive style={{ fontSize: 22, color: '#8f8f8f' }} />
          Shortcut
        </MenuItem>
      </Menu>
    </>
  );
};
